import { OracleType, OracleProvider, TokenListMetadata } from "@bridgesplit/abf-sdk";
import { SortDirection } from "@bridgesplit/utils";
import Decimal from "decimal.js";

import { StrategyExpandedInfo } from "./strategy";
import { RewardMint, StakeAccount, VaultAddress } from "./alias";
import type { ApiUserVaultsResponse } from "./api/vault";
import {
    LendingVaultMetadata,
    ParsedLendingVaultInfo,
    ParsedTimeLock,
    ParsedVaultRewardsSchedule,
    VaultDepositsAggregate
} from "./domain";
import { Replace } from "../reducers";
import { LendingVaultMetadataApi } from "./api";

export interface VaultLtvInfos {
    collateralMetadata: TokenListMetadata;
    maxCollateralAllocationPct: Decimal | null;
    durationToLtvInfo: Record<string, VaultLtvInfo>; //Key is the serialized strategyDuration
}

export interface VaultLtvInfo {
    apy?: string; //If no APY is set, it is disabled, this APY comes from stratgy terms
    marketInfo: VaultLtvMarketInfo | undefined;
}

export interface VaultLtvMarketInfo {
    ltv: number;
    liquidationThreshold: number;
    oracleAccount: string;
    /** Written back on chain by collateral updates; not for display. */
    oracleType: OracleType;
    oracleProvider: OracleProvider;
}

export interface UserVaultPositionExpanded {
    vaultExpanded: LendingVaultExpanded;
    vaultLpBalance: number;
    /** LP vault token posted as loan collateral (open loans), UI amount (same decimal handling as `vaultLpBalance`). */
    lpCollateralDeposits: number;
    address: string;
    index: number;
    active: boolean;
    totalDeposits: number;
    totalWithdrawals: number;
    startTime: number;
    lastInteractedTimestamp: number;

    positionValueUsd: number;
    availableValueUsd: number;
    collateralValueUsd: number;
    pnlUsd: number;
    baseApy: number;
    rewardApy: number;
    collateralYieldApy: number;
    totalApy: number;
}

export interface UserVaultPnl {
    vaultPnl: number | undefined;
    lastInteractedTimestamp: number | undefined;
}

export interface UserVaultPositionAmounts {
    totalAmount: UserVaultLpBalance;
    amountAvailable: UserVaultLpBalance;
    lpCollateralDeposits: UserVaultLpBalance;
}

export interface UserVaultLpBalance {
    amountLp: number;
    amountPrincipal: number;
    amountUsd: number;
}

export type LendingVaultMetadataExpanded = Replace<
    LendingVaultMetadata,
    {
        isGenesisVault: boolean;
    }
>;

export type ParsedVaultRewardsScheduleExpanded = Replace<
    ParsedVaultRewardsSchedule,
    {
        rewardMetadata: TokenListMetadata;
    }
>;

export type LendingVaultInfo = Replace<
    ParsedLendingVaultInfo,
    {
        vaultMetadata: LendingVaultMetadataExpanded;
        // null for coming-soon vaults that have no live strategy on-chain
        vaultStrategy: StrategyExpandedInfo | null;
        vaultRewardsSchedules: ParsedVaultRewardsScheduleExpanded[];
    }
>;

export type LendingVaultExpanded = Replace<
    LendingVaultInfo,
    {
        principalMetadata: TokenListMetadata;
        lpMetadata: TokenListMetadata | null;
        ltvInfos: Record<string, VaultLtvInfos> | undefined; // Key is collateral mint address.
        pendingTerms: ParsedTimeLock[];
    }
>;

export type UserVaultsResponse = ApiUserVaultsResponse;

/** Grouped lend vault portfolio row (`POST /user/vaults`) with resolved principal metadata — same layering as {@link UserStrategyGroup}. */
export interface UserVaultGroup {
    principalMint: string;
    principalMetadata: TokenListMetadata;
    aggregate: VaultDepositsAggregate;
    /** Sum of `positionValueNative` across the group's rows — total
     *  user-held value in `principalMint`'s native base units (decimals
     *  not applied). */
    totalValueNative: number;
    /** Subset of `totalValueNative` that is unlocked (staked, not pledged
     *  as loan collateral). Native base units. */
    availableValueNative: number;
    /** Subset of `totalValueNative` currently pledged as loan collateral.
     *  Native base units. */
    collateralValueNative: number;
}

export interface MarketInformationsFilter {
    principalMints: string[];
    addresses: string[];
    verified?: boolean; //When verified is true we query the presets market information table
    // When false the BE omits the heavy `marketInformationAssetInfo` (per-asset risk/oracle terms)
    // but still returns identity + `underlyingAssets`. Defaults to true server-side. Used by the eager
    // app-load prefetch to slim the payload; asset-info consumers fetch full data scoped on demand.
    includeAssetInfo?: boolean;
}

export interface ParsedLtvData {
    principalMint: string;
    ltv: number;
    liquidationThreshold: number;
}

export interface LendVaultInfoFilter {
    vaultAddresses?: string[];
    vaultIdentifiers?: string[];
    principalMints?: string[];
    lpMints?: string[];
    depositsEnabled?: boolean;
    includeRewards?: boolean;
    // Inline each vault's strategy USD/APY summary in the response so the FE can skip /strategy/summaries.
    // (Per-collateral assetTerms are always present on /lending_vaults/info, so no separate flag is needed.)
    includeStrategySummaries?: boolean;
}

export interface LendVaultsPagination {
    sortType: LendVaultSortType;
    sortDirection: SortDirection;
    page: number;
    pageSize: number;
}
export interface LendVaultStakeFilter {
    sort: StakeSortType;
    sortDirection: SortDirection;
    page: number;
    pageSize: number;
}

export type FetchLendVaultsInfoParams = LendVaultsPagination & LendVaultInfoFilter;
export type FetchLendVaultStakeParams = LendVaultStakeFilter & LendVaultInfoFilter;

export enum LendVaultSortType {
    TotalDeposits,
    WAvgApy
}

export enum StakeSortType {
    StartTime,
    EndTime,
    AmountStaked,
    WAvgApy
}

export interface FetchLendVaultsHistoryParams {
    vaultAddress: string;
    timeLookback: number;
    sampleIntervalSecs: number;
}

export interface LendingVaultsHistory {
    timestamp: number;
    cumulativeLoanVolume: number;
    cumulativeLoanCount: number;
    activeLoanCount?: number;
    principalDeployed: number;
    wAvgApy: number;
    externalRewardsApy?: number;
    utilizationRate?: number;
    netAssetValue: number;
    // Both raw base-unit amounts (principal decimals). LP price = netAssetValue / lpSupply; see
    // deriveLpPrice in VaultPositionCharts. `virtualPrice` is the BE's u64 of that ratio, which
    // truncates to an integer, so callers should derive from netAssetValue / lpSupply instead.
    virtualPrice?: number;
    lpSupply?: number;
}

export interface LendingVaultAllocationExpanded extends LendingVaultAllocation {
    collateralMetadata: TokenListMetadata;
}

export interface LendingVaultAllocation {
    collateralMint: string;
    activeLoanCount: number;
    totalPrincipalDeployed: number;
    wAvgApy: number;
    collateralDepositedUsd: number | null;
}

export type UserLendingVaultFilter = FetchLendVaultStakeParams & {
    status: UserVaultPositionStatus;
    // Required on the filter body. Callers get the active wallet from `useLoopscaleWalletContext`.
    userAddress: string;
};

export enum UserVaultPositionStatus {
    Active,
    Inactive
}

export enum ParsedAllowedStakeDuration {
    ZeroDays,
    OneMonth,
    ThreeMonths,
    SixMonths,
    OneYear
}

export interface UserExternalRewardsFilter {
    vaultAddresses: string[];
    stakeAccounts: string[];
    stakeStatus?: number;
    rewardMints: string[];
}

export interface UserExternalRewardsPnlInfo {
    rewardMint: string;
    claimedRewards: number;
    pendingRewards: number;
}

export interface UserExternalRewardsInfoExpanded extends UserExternalRewardsPnlInfo {
    rewardMetadata: TokenListMetadata;
}

export type UserVaultExternalRewardsPnlResponse = Record<
    VaultAddress,
    Record<StakeAccount, Record<RewardMint, UserExternalRewardsInfoExpanded>>
>;

// Flattened version of UserVaultExternalRewardsPnlResponse for use in the UI
export type AggregatedUserExternalRewardsPnlResponse = Record<
    VaultAddress,
    Record<RewardMint, UserExternalRewardsInfoExpanded>
>;

export enum VaultActionType {
    Deposit = "deposit",
    Withdrawal = "withdrawal"
}

export interface ParsedLendVaultAction {
    actionType: VaultActionType;
    amount: number;
    timestamp: number;
    vaultAddress: string;
    transactionSignature: string;
}

export interface VaultDepositSummary {
    sumOfDeposits: number;
    sumOfWithdrawals: number;
    lastInteractedTime?: number;
    vaultActions: ParsedLendVaultAction[];
}

export type UserPositionDepositSummaryResponse = Record<string, VaultDepositSummary>;

export interface UserPositionDepositSummaryParams {
    vaultAddresses?: string[];
    stakeAccounts?: string[];
}

export interface VaultApyBreakdown {
    baseApy: number;
    rewardApy: number;
    collateralYield: number;
    totalApy: number;
}

export interface UpdateVaultMetadataParams {
    vaultAddress: string;
    metadata: LendingVaultMetadataApi;
    timestamp: number;
    signature: string;
}

export interface MaxVaultWithdraw {
    maxWithdrawPrincipal: number;
    userBalancePrincipal: number;
    globalMaxWithdrawPrincipal: number;
    maxWithdrawLp: number;
    userBalanceLp: number;
    globalMaxWithdrawLp: number;
    maxWithdrawCapPrincipal: number;
    maxWithdrawCapLp: number;
}
